import React from "react";
import cx from "classnames";
import { Pill, Tooltip } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { getScheduledBlocksState, ScheduledBlocksState } from "@utils/scheduledBlocks";
import styles from "./scheduledBlocksBadge.module.scss";

type PillColor = React.ComponentProps<typeof Pill>["color"];
type PillTextColor = React.ComponentProps<typeof Pill>["textColor"];

interface ScheduledBlocksBadgeProps {
  assigned: number;
  planned?: number | null;
  className?: string;
}

const pillColorByState: {
  [state in ScheduledBlocksState]: { color: PillColor; textColor: PillTextColor };
} = {
  success: { color: "success-10", textColor: "success-60" },
  warning: { color: "warning-10", textColor: "warning-60" },
  error: { color: "error-opacity", textColor: "error-60" },
  neutral: { color: "gray-10", textColor: "neutral-80" },
};

/**
 * Section scheduled-blocks (UPD) balance badge — `{assigned}/{planned} Bloques`
 * (or `-/-` when there is no planned value) with a per-state tooltip. Rendered on
 * each section grouper header in the assignment editor (CHS-2785 / EDH-014).
 */
const ScheduledBlocksBadge: React.FC<ScheduledBlocksBadgeProps> = ({
  assigned,
  planned,
  className,
}) => {
  const { t } = useTranslation();
  const state = getScheduledBlocksState({ assigned, planned });
  const { color, textColor } = pillColorByState[state];

  const value = planned === null || planned === undefined ? "-/-" : `${assigned}/${planned}`;

  return (
    <Tooltip
      className={cx(className, styles.scheduledBlocksBadge)}
      placement="bottom"
      label={t(`assignation-edit.scheduled-blocks.tooltips.${state}`)}
    >
      <Pill color={color} textColor={textColor} className={styles.scheduledBlocksBadge_pill}>
        {`${value} ${t("assignation-edit.scheduled-blocks.blocks-label")}`}
      </Pill>
    </Tooltip>
  );
};

export default ScheduledBlocksBadge;
